import React, { memo, useCallback } from 'react'

import { Editor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import { TbCircleCheck } from 'react-icons/tb'

import { Toolbar } from '@/components/common/TextArea/RichTextArea/menus/components/Toolbar'
import { useTextMenuCommands } from '@/components/common/TextArea/RichTextArea/menus/TextMenu'
import type { ShouldShowProps } from '@/components/common/TextArea/RichTextArea/menus/TextMenu/types'
import type { MenuProps } from '@/components/common/TextArea/RichTextArea/menus/types'

import type { UUID } from '@/types/Id'

interface CommentApprovalMenuProps extends MenuProps {
  onApproveComments?: (ids: UUID[]) => void
}

const MemoButton = memo(Toolbar.Button)

type OptionsProps = NonNullable<
  React.ComponentProps<typeof BubbleMenu>['options']
>

const APPROVAL_MENU_OPTIONS: OptionsProps = {
  placement: 'top-start' as const,
  offset: 8,
  flip: {
    fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'] as const,
  },
}

export const useCommentApprovalMenuState = (editor: Editor | null) => {
  const shouldShow = useCallback(
    ({ view, from, to }: ShouldShowProps) => {
      if (!view || !editor) {
        return false
      }

      // don't show if there's a text selection (TextMenu will handle that)
      if (from !== to) {
        return false
      }

      const pos = from || 0
      const node = editor.state.doc.nodeAt(pos)

      if (!node) return false

      // show only if there's a needs-review comment at cursor position that isn't resolved
      const commentMark = node.marks.find(
        (mark) =>
          mark.type.name === 'comment' &&
          mark.attrs.needsReview === true &&
          mark.attrs.resolved === false,
      )

      return !!commentMark
    },
    [editor],
  )
  return { shouldShow }
}

export const CommentApprovalMenu: React.FC<CommentApprovalMenuProps> = ({
  editor,
  appendTo,
  onApproveComments: approveCommentsCallback,
}) => {
  const { shouldShow } = useCommentApprovalMenuState(editor)
  const { onApproveComment } = useTextMenuCommands({
    editor,
    onApproveCommentsCallback: approveCommentsCallback,
  })

  // wrapping this in a dev prevents a failed to remove node React error when
  // any component that uses this menu is unmounted;
  // https://github.com/ueberdosis/tiptap/issues/3784
  return (
    <div>
      <BubbleMenu
        key="approvalMenu"
        options={APPROVAL_MENU_OPTIONS}
        editor={editor}
        pluginKey="approvalMenu"
        shouldShow={shouldShow}
        updateDelay={100}
      >
        <Toolbar.Wrapper className="shadow-md p-[3px]">
          <MemoButton
            className="flex items-center gap-[5px] rounded-[4px] text-green-600 dark:text-green-500"
            onClick={onApproveComment}
          >
            <TbCircleCheck className="w-[18px] h-[18px]" />
            <span className="text-sm font-medium">Approve</span>
          </MemoButton>
        </Toolbar.Wrapper>
      </BubbleMenu>
    </div>
  )
}
